import React, { useEffect, useState, useCallback } from "react";
import useApp from "../../hooks/useAppContext";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/axios";
import { toast } from "react-toastify";
import { FaArrowLeft, FaSave, FaSpinner } from "react-icons/fa";
import "./admPatrocinadoresList.css";
import "./admFormPatrocinadores.css";

const TIPO_AGENDA = "geral";

const AdmFormAgenda = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getEventoId, getDiasEvento } = useApp();

  const [nome, setNome] = useState("");
  const [data, setData] = useState("");
  const [dtHoraInicio, setDtHoraInicio] = useState("");
  const [dtHoraFim, setDtHoraFim] = useState("");
  const [detalhe, setDetalhe] = useState("");
  const [local, setLocal] = useState("");
  const [diasEvento, setDiasEvento] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const limparFormulario = useCallback(() => {
    setNome("");
    setData("");
    setDtHoraInicio("");
    setDtHoraFim("");
    setDetalhe("");
    setLocal("");
  }, []);

  const carregarDias = useCallback(async () => {
    const dados = await getDiasEvento();
    const dias = dados?.dias || [];
    setDiasEvento(dias);
    return dias;
  }, [getDiasEvento]);

  const carregarEvento = useCallback(
    async (eventoId) => {
      const evento = await getEventoId(eventoId);
      if (!evento) return;

      setNome(evento.nome || "");
      setData(
        evento.dt_hora_inicio ? evento.dt_hora_inicio.substring(0, 10) : ""
      );
      setDtHoraInicio(
        evento.dt_hora_inicio ? evento.dt_hora_inicio.substring(11, 16) : ""
      );
      setDtHoraFim(
        evento.dt_hora_fim ? evento.dt_hora_fim.substring(11, 16) : ""
      );
      setDetalhe(evento.detalhe || "");
      setLocal(evento.local || "");
    },
    [getEventoId]
  );

  useEffect(() => {
    const iniciar = async () => {
      setIsLoading(true);
      try {
        limparFormulario();
        await carregarDias();
        if (id) {
          await carregarEvento(id);
        }
      } catch (err) {
        console.error("Erro ao carregar formulário de agenda:", err);
        toast.error("Erro ao carregar o formulário.");
      } finally {
        setIsLoading(false);
      }
    };
    iniciar();
  }, [id, limparFormulario, carregarDias, carregarEvento]);

  const montarDataHora = (dataIso, hora) => {
    if (!dataIso || !hora) return null;
    return `${dataIso} ${hora}:00`;
  };

  const handleSalvarEvento = async () => {
    if (!nome?.trim()) {
      toast.error("Informe o nome da agenda.");
      return;
    }
    if (!data) {
      toast.error("Selecione a data do evento.");
      return;
    }
    if (!dtHoraInicio || !dtHoraFim) {
      toast.error("Informe horário de início e fim.");
      return;
    }
    if (!local?.trim()) {
      toast.error("Informe o local.");
      return;
    }

    const dataInicio = montarDataHora(data, dtHoraInicio);
    const dataFim = montarDataHora(data, dtHoraFim);

    const dados = {
      nome: nome.trim(),
      tipo: TIPO_AGENDA,
      dt_hora_inicio: dataInicio,
      dt_hora_fim: dataFim,
      detalhe: detalhe?.trim() || undefined,
      local: local.trim(),
    };

    setIsSaving(true);
    try {
      if (id) {
        const resp = await api.put(`/agenda-update/${id}`, dados);
        toast.info(resp.data.mensagem, { theme: "colored" });
        await carregarEvento(id);
      } else {
        const resp = await api.post("/agenda-create", dados);
        toast.info(resp.data.mensagem, { theme: "colored" });
        limparFormulario();
        const diaSelecionado = diasEvento.find((item) => item.data === data);
        navigate(
          `/private/adm/agenda/${diaSelecionado?.dia || data.substring(8, 10)}`
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.mensagem || "Erro ao salvar a agenda.",
        { theme: "colored" }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const titulo = id ? "Editar agenda" : "Nova agenda";
  const lead = id
    ? "Atualize os dados do item da programação geral do evento."
    : "Cadastre um item da programação geral. O tipo é definido automaticamente.";

  const labelDataSelecionada = (item) => {
    if (!item?.data) return item?.label || "";
    const [ano, mes, dia] = item.data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <div className="container-adm-main">
      <div className="apl-page apl-page--wide-form">
        <header className="apl-header" aria-label="Cabeçalho do formulário">
          <div className="apl-header-text">
            <h1>{titulo}</h1>
            <p>{lead}</p>
          </div>
          <div className="apl-toolbar apl-toolbar--inline">
            <div className="apl-toolbar-actions">
              <button
                type="button"
                className="apl-btn apl-btn--primary"
                onClick={handleSalvarEvento}
                disabled={isSaving || isLoading}
              >
                {isSaving ? (
                  <FaSpinner className="fa-spin" size={14} aria-hidden />
                ) : (
                  <FaSave size={14} aria-hidden />
                )}
                {isSaving ? "Salvando…" : "Salvar"}
              </button>
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {isLoading ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando formulário…</span>
              </div>
            </div>
          ) : (
            <section
              className="apl-card apf-form-card"
              aria-label="Dados da agenda"
            >
              <h2 className="apf-section-title">Dados da programação</h2>

              <div className="row">
                <div className="col-lg-8 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_nome_agenda"
                  >
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="input_nome_agenda"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    autoFocus
                    required
                    placeholder="Ex.: Abertura oficial"
                  />
                </div>

                <div className="col-lg-4 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_data_agenda"
                  >
                    Data <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-control form-control-lg"
                    id="input_data_agenda"
                    value={data}
                    onChange={(e) => setData(e.target.value)}
                    required
                  >
                    <option value="">Selecione o dia</option>
                    {diasEvento.map((item) => (
                      <option key={item.data} value={item.data}>
                        {labelDataSelecionada(item)}
                      </option>
                    ))}
                  </select>
                  {diasEvento.length === 0 ? (
                    <p className="small text-muted mt-1 mb-0">
                      Nenhuma data de evento ativa ou em desenvolvimento.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="row">
                <div className="col-lg-3 col-md-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_hora_inicio"
                  >
                    Hora início <span className="text-danger">*</span>
                  </label>
                  <input
                    type="time"
                    className="form-control form-control-lg"
                    id="input_hora_inicio"
                    value={dtHoraInicio}
                    onChange={(e) => setDtHoraInicio(e.target.value)}
                    required
                  />
                </div>

                <div className="col-lg-3 col-md-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_hora_fim"
                  >
                    Hora fim <span className="text-danger">*</span>
                  </label>
                  <input
                    type="time"
                    className="form-control form-control-lg"
                    id="input_hora_fim"
                    value={dtHoraFim}
                    onChange={(e) => setDtHoraFim(e.target.value)}
                    required
                  />
                </div>

                <div className="col-lg-6 col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_local_agenda"
                  >
                    Local <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    id="input_local_agenda"
                    value={local}
                    onChange={(e) => setLocal(e.target.value)}
                    required
                    placeholder="Ex.: Auditório principal"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3">
                  <label
                    className="form-label label-input"
                    htmlFor="input_detalhe_agenda"
                  >
                    Detalhe
                  </label>
                  <textarea
                    className="form-control"
                    rows={5}
                    id="input_detalhe_agenda"
                    value={detalhe}
                    onChange={(e) => setDetalhe(e.target.value)}
                    placeholder="Descrição opcional do item da agenda"
                  />
                </div>
              </div>

              <div className="apf-form-actions">
                <button
                  type="button"
                  className="apl-btn apl-btn--ghost"
                  onClick={() => navigate(-1)}
                  disabled={isSaving}
                >
                  <FaArrowLeft size={14} aria-hidden />
                  Cancelar
                </button>
                <button
                  type="button"
                  className="apl-btn apl-btn--primary"
                  onClick={handleSalvarEvento}
                  disabled={isSaving || isLoading}
                >
                  {isSaving ? (
                    <FaSpinner className="fa-spin" size={14} aria-hidden />
                  ) : (
                    <FaSave size={14} aria-hidden />
                  )}
                  {isSaving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmFormAgenda;
